<template>
  <Dialog
    v-model:visible="visibleModel"
    :style="{ width: '450px' }"
    header="Nueva solicitud de licencia o permiso"
    :modal="true"
    class="p-fluid"
  >
    <div class="field">
      <label for="licenseType" class="font-medium text-700"
        >Tipo de licencia <span class="text-red-500">*</span></label
      >
      <Dropdown
        id="licenseType"
        v-model="license.licenseType"
        :options="licenseDefinitions?.licensesTypes"
        required="true"
        placeholder="Selecciona un tipo de licencia"
        :loading="!licenseDefinitions?.licensesTypes"
        @change="emit('license-type-change', $event)"
      >
        <template #value="{ value, placeholder }">
          <span v-if="value" class="text-sm text-600 font-medium">
            {{ value.entity + ' - ' + value.name }}
          </span>
          <span v-else>
            {{ placeholder }}
          </span>
        </template>
        <template #option="{ option }">
          {{ option.entity + ' - ' + option.name }}
        </template>
      </Dropdown>
    </div>
    <div class="field" v-if="license?.licenseType?.enable_continuity">
      <label for="licenseNature" class="font-medium text-700"
        >Naturaleza de la licencia <span class="text-red-500">*</span></label
      >
      <Dropdown
        id="licenseNature"
        v-model="license.licenseNature"
        :options="licenseDefinitions?.licenseNatures"
        required="true"
        placeholder="Selecciona una naturaleza de licencia"
      >
        <template #value="{ value, placeholder }">
          <span v-if="value" class="text-sm text-600 font-medium">
            {{ value.name }}
          </span>
          <span v-else>
            {{ placeholder }}
          </span>
        </template>
        <template #option="{ option }">
          {{ option.name }}
        </template>
      </Dropdown>
    </div>
    <div class="field">
      <label for="dataInit" class="font-medium text-700"
        >Fecha inicial <span class="text-red-500">*</span></label
      >
      <Calendar
        id="dataInit"
        v-model="license.dateInit"
        required="true"
        :disabled="license.licenseType === null"
        :class="{ 'p-invalid': submitted && !license.dateInit }"
        :disabledDays="disabledDays"
        :disabled-dates="disabledDates"
        date-format="dd/mm/yy"
      >
        <template #date="{ date }">
          <div class="flex flex-column">
            <span>{{ date.day }}</span>
            <span :class="getBadge(date)"></span>
          </div>
        </template>
        <template #footer>
          <div class="ml-6 mt-3">
            <div class="flex align-items-center">
              <div class="mr-2 license-pending"></div>
              <label class="text-sm font-semibold text-600 mt-2"
                >Licencias pendientes</label
              >
            </div>

            <div class="flex align-items-center">
              <div class="mr-2 license-approved"></div>
              <label class="text-sm font-semibold text-600 mt-2"
                >Licencias aprobadas</label
              >
            </div>

            <div class="flex align-items-center">
              <div class="mr-2 vacation-approved"></div>
              <label class="text-sm font-semibold text-600 mt-2"
                >Vacaciones aprobadas</label
              >
            </div>

            <div class="flex align-items-center">
              <div class="mr-2 vacation-pending"></div>
              <label class="text-sm font-semibold text-600 mt-2"
                >Vacaciones pendientes</label
              >
            </div>

            <div class="flex align-items-center">
              <div class="mr-2 holiday"></div>
              <label class="text-sm font-semibold text-600 mt-2"
                >Feriados</label
              >
            </div>
          </div>
        </template>
      </Calendar>
    </div>
    <div class="field">
      <label for="cantDays" class="font-medium text-700"
        >Cantidad de dias <span class="text-red-500">*</span></label
      >
      <InputNumber
        id="cantDays"
        v-model="license.quantityDays"
        :class="{ 'p-invalid': submitted && !license.quantityDays }"
      />
    </div>
    <div class="field">
      <label for="dateEnd" class="font-medium text-700"
        >Fecha final <span class="text-red-500">*</span></label
      >
      <Calendar
        id="dateEnd"
        v-model="license.dateEnd"
        :class="{ 'p-invalid': submitted && !license.dateEnd }"
        disabled
        date-format="dd/mm/yy"
      />
    </div>
    <div class="field" v-if="license.licenseType?.allow_primary_attachment">
      <label for="adjunto" class="font-medium text-700"
        >Adjuntar documento(s)
        <span
          v-if="license.licenseType.obligatory_primary_attachment"
          class="text-red-500"
          >*</span
        ></label
      >
      <FileUpload
        mode="basic"
        class="font-light text-sm w-auto text-center"
        style="font-size: 12px !important"
        custom-upload
        @uploader="emit('remove-file')"
        @select="emit('select-file', $event)"
        :multiple="true"
        upload-icon="pi pi-paperclip"
      />
    </div>
    <div class="field">
      <label for="observation" class="font-medium text-700"
        >Observación
        <span class="text-sm font-light text-gray-600 cursive">
          ({{ Math.max(0, 300 - (license.observation?.length ?? 0)) }}
          restantes)
        </span>
        <span class="text-red-500">*</span></label
      >
      <Textarea
        id="observation"
        v-model="license.observation"
        required="true"
        :class="{ 'p-invalid': submitted && !license.observation }"
      />
    </div>
    <template #footer>
      <Button
        label="Cancelar"
        icon="pi pi-times"
        severity="secondary"
        class="text-sm font-light mr-2"
        @click="emit('cancel')"
      />
      <Button
        label="Solicitar"
        icon="pi pi-check"
        class="text-sm font-light mr-2"
        @click="emit('save')"
        :disabled="!isValidToSave"
      />
    </template>
  </Dialog>
</template>
<script setup lang="ts">
  import { computed, PropType, watch } from 'vue'
  import Dialog from 'primevue/dialog'
  import Dropdown from 'primevue/dropdown'
  import Calendar from 'primevue/calendar'
  import InputNumber from 'primevue/inputnumber'
  import Textarea from 'primevue/textarea'
  import FileUpload from 'primevue/fileupload'
  import Button from 'primevue/button'
  import GetLicenseTypeDto from '../services/interfaces/GetLicenseTypeDto'
  import LicenseForm from '../models/LicenseForm'

  const props = defineProps({
    visible: { type: Boolean, required: true },
    license: { type: Object as PropType<LicenseForm>, required: true },
    licenseDefinitions: Object as PropType<GetLicenseTypeDto>,
    submitted: { type: Boolean, default: false },
    disabledDays: { type: Array as PropType<number[]>, default: () => [] },
    disabledDates: { type: Array as PropType<Date[]>, default: () => [] },
    getBadge: {
      type: Function as PropType<(date: any) => string>,
      required: true,
    },
    isValidToSave: { type: Boolean, default: false },
  })

  const emit = defineEmits([
    'update:visible',
    'license-type-change',
    'calculate-date-end',
    'select-file',
    'remove-file',
    'cancel',
    'save',
  ])

  const visibleModel = computed({
    get: () => props.visible,
    set: (value) => emit('update:visible', value),
  })

  watch(
    () => [props.license.dateInit, props.license.quantityDays],
    ([newDate, newQty], [oldDate, oldQty]) => {
      if (newDate === oldDate && newQty === oldQty) return
      if (newDate && newQty) emit('calculate-date-end')
    }
  )
</script>
<style scoped>
  .vacation-approved {
    background-color: #4caf50;
    border-radius: 50%;
    height: 10px;
    width: 10px;
    border: 1px solid #4caf50;
  }

  .vacation-pending {
    background-color: #ff9800;
    border-radius: 50%;
    height: 10px;
    width: 10px;
    border: 1px solid #ff9800;
  }

  .license-approved {
    background-color: rgb(228, 57, 57);
    border-radius: 50%;
    height: 10px;
    width: 10px;
    border: 1px solid rgb(228, 57, 57);
  }

  .license-pending {
    background-color: rgb(240, 243, 56);
    border-radius: 50%;
    height: 10px;
    width: 10px;
    border: 1px solid rgb(240, 243, 56);
  }

  .holiday {
    background-color: rgb(56, 56, 243);
    border-radius: 50%;
    height: 10px;
    width: 10px;
    border: 1px solid rgb(56, 56, 243);
  }
</style>
